import { Image1Channel } from 'preprocessor';

/**
 * @typedef {import('./locator.js').Point } Point
 * @typedef {import('./locator.js').QRLocation } QRLocation
 *
 * @callback Sampler1Channel
 * @param {number} x - The x coordinate
 * @param {number} y - The y coordinate
 * @returns {PixelCoordinate} The sampled value
 */

/**
 * @typedef {Object} PerspectiveTransformMatrix
 * @property  {number} a11;
 * @property  {number} a21;
 * @property  {number} a31;
 * @property  {number} a12;
 * @property  {number} a22;
 * @property  {number} a32;
 * @property  {number} a13;
 * @property  {number} a23;
 * @property  {number} a33;
 */

/**
 *  square to quadrilateral matrix
 * @param {Point} p1
 * @param {Point} p2
 * @param {Point} p3
 * @param {Point} p4
 * @returns {PerspectiveTransformMatrix}
 */
function squareToQuadrilateral(p1, p2, p3, p4) {
  const dx3 = p1.x - p2.x + p3.x - p4.x;
  const dy3 = p1.y - p2.y + p3.y - p4.y;
  if (dx3 === 0 && dy3 === 0) {
    // Affine
    return {
      a11: p2.x - p1.x,
      a12: p2.y - p1.y,
      a13: 0,
      a21: p3.x - p2.x,
      a22: p3.y - p2.y,
      a23: 0,
      a31: p1.x,
      a32: p1.y,
      a33: 1,
    };
  } else {
    const dx1 = p2.x - p3.x;
    const dx2 = p4.x - p3.x;
    const dy1 = p2.y - p3.y;
    const dy2 = p4.y - p3.y;
    const denominator = dx1 * dy2 - dx2 * dy1;
    const a13 = (dx3 * dy2 - dx2 * dy3) / denominator;
    const a23 = (dx1 * dy3 - dx3 * dy1) / denominator;
    return {
      a11: p2.x - p1.x + a13 * p2.x,
      a12: p2.y - p1.y + a13 * p2.y,
      a13,
      a21: p4.x - p1.x + a23 * p4.x,
      a22: p4.y - p1.y + a23 * p4.y,
      a23,
      a31: p1.x,
      a32: p1.y,
      a33: 1,
    };
  }
}

/**
 * quadrilateral to square matrix
 * @param {Point} p1
 * @param {Point} p2
 * @param {Point} p3
 * @param {Point} p4
 * @returns {PerspectiveTransformMatrix} The matrix
 */
function quadrilateralToSquare(p1, p2, p3, p4) {
  // Here, the adjoint serves as the inverse:
  const sToQ = squareToQuadrilateral(p1, p2, p3, p4);
  return {
    a11: sToQ.a22 * sToQ.a33 - sToQ.a23 * sToQ.a32,
    a12: sToQ.a13 * sToQ.a32 - sToQ.a12 * sToQ.a33,
    a13: sToQ.a12 * sToQ.a23 - sToQ.a13 * sToQ.a22,
    a21: sToQ.a23 * sToQ.a31 - sToQ.a21 * sToQ.a33,
    a22: sToQ.a11 * sToQ.a33 - sToQ.a13 * sToQ.a31,
    a23: sToQ.a13 * sToQ.a21 - sToQ.a11 * sToQ.a23,
    a31: sToQ.a21 * sToQ.a32 - sToQ.a22 * sToQ.a31,
    a32: sToQ.a12 * sToQ.a31 - sToQ.a11 * sToQ.a32,
    a33: sToQ.a11 * sToQ.a22 - sToQ.a12 * sToQ.a21,
  };
}

/**
 * Times 2 matrices
 * @param {PerspectiveTransformMatrix} a - The first matrix
 * @param {PerspectiveTransformMatrix} b - The second matrix
 * @returns {PerspectiveTransformMatrix} The result of the multiplication
 */
function times(a, b) {
  return {
    a11: a.a11 * b.a11 + a.a21 * b.a12 + a.a31 * b.a13,
    a12: a.a12 * b.a11 + a.a22 * b.a12 + a.a32 * b.a13,
    a13: a.a13 * b.a11 + a.a23 * b.a12 + a.a33 * b.a13,
    a21: a.a11 * b.a21 + a.a21 * b.a22 + a.a31 * b.a23,
    a22: a.a12 * b.a21 + a.a22 * b.a22 + a.a32 * b.a23,
    a23: a.a13 * b.a21 + a.a23 * b.a22 + a.a33 * b.a23,
    a31: a.a11 * b.a31 + a.a21 * b.a32 + a.a31 * b.a33,
    a32: a.a12 * b.a31 + a.a22 * b.a32 + a.a32 * b.a33,
    a33: a.a13 * b.a31 + a.a23 * b.a32 + a.a33 * b.a33,
  };
}

/**
 * Compute the final perspective transformation matrix
 * @param {QRLocation} location - The location of the QR code
 * @returns {PerspectiveTransformMatrix}
 */
export function computeTransformationMatrix(location) {
  const qToS = quadrilateralToSquare(
    { x: 3.5, y: 3.5 },
    { x: location.dimension - 3.5, y: 3.5 },
    { x: location.dimension - 6.5, y: location.dimension - 6.5 },
    { x: 3.5, y: location.dimension - 3.5 },
  );
  const sToQ = squareToQuadrilateral(
    location.topLeft,
    location.topRight,
    location.alignmentPattern,
    location.bottomLeft,
  );
  const transform = times(sToQ, qToS);
  return transform;
}

/**
 * Generate a sampler function from a perspective transformation, the result function will be able to sample from the orignal image when given a coordinate in the target coordinate
 * @param {PerspectiveTransformMatrix} transform - The perspective transformation matrix
 * @returns {Sampler1Channel} The transform function
 */
export const generateSampler = (transform) => (x, y) => {
  const denominator = transform.a13 * x + transform.a23 * y + transform.a33;
  return {
    x: (transform.a11 * x + transform.a21 * y + transform.a31) / denominator,
    y: (transform.a12 * x + transform.a22 * y + transform.a32) / denominator,
  };
};

/**
 * @typedef ExtractedMatrix
 * @property {Image1Channel} extractedImage - The extracted image
 * @property {Sampler1Channel} mappingFunction - The mapping function
 */
/**
 * Extract a QR code from an image
 * @param {Image1Channel} image - The bit matrix of the image
 * @param {QRLocation} location - The location of the QR code
 * @returns {ExtractedMatrix} The extracted QR code
 */
export function extract(image, location) {
  const qToS = quadrilateralToSquare(
    { x: 3.5, y: 3.5 },
    { x: location.dimension - 3.5, y: 3.5 },
    { x: location.dimension - 6.5, y: location.dimension - 6.5 },
    { x: 3.5, y: location.dimension - 3.5 },
  );
  const sToQ = squareToQuadrilateral(
    location.topLeft,
    location.topRight,
    location.alignmentPattern,
    location.bottomLeft,
  );
  const transform = times(sToQ, qToS);

  const mappingFunction = (x, y) => {
    const denominator = transform.a13 * x + transform.a23 * y + transform.a33;
    return {
      x: (transform.a11 * x + transform.a21 * y + transform.a31) / denominator,
      y: (transform.a12 * x + transform.a22 * y + transform.a32) / denominator,
    };
  };

  // sampling from original image and return the extracted image
  const extractedImage = Image1Channel.fromSampler(
    mappingFunction,
    image,
    location.dimension,
    location.dimension,
  );

  return {
    extractedImage,
    mappingFunction,
  };
}
